import {
  AttachFileToVersionBody,
  BulkUpdateSubscriptionsBody,
  CreateProductBody,
  ExportUpdatesResponse,
  ExportUpdatesToTracexResponse,
  GetUpdateDetailResponse,
  ListAllDocumentsQuery,
  ListReferencesQuery,
  ListStandardSourcesQuery,
  ListUpdatesQuery,
  ListUpdatesResponse,
  OnboardingGenerateProfileBody,
  UpdateContextStatusBody,
  UpdateProductBody,
  UpdateProfileBody,
  UpdateReferenceSubscriptionBody,
} from '@ditto/shared';
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { useAuthenticatedClient, useProtectedQuery } from './useApi';

const queryKeys = {
  all: ['regulatoryWatch'],
  sources: () => [...queryKeys.all, 'sources'],
  sourcesWithSubscriptions: () => [
    ...queryKeys.all,
    'sourcesWithSubscriptions',
  ],
  standardSources: (query?: ListStandardSourcesQuery) => [
    ...queryKeys.all,
    'standardSources',
    query,
  ],
  updates: (filters?: ListUpdatesQuery) => [
    ...queryKeys.all,
    'updates',
    filters,
  ],
  updateDetail: (id: string) => [...queryKeys.all, 'updateDetail', id],
  profile: () => [...queryKeys.all, 'profile'],
  references: (filters?: ListReferencesQuery) => [
    ...queryKeys.all,
    'references',
    filters,
  ],
  referenceSubscriptions: (sourceId: string) => [
    ...queryKeys.all,
    'referenceSubscriptions',
    sourceId,
  ],
  allDocuments: (filters?: ListAllDocumentsQuery) => [
    ...queryKeys.all,
    'allDocuments',
    filters,
  ],
  referenceDetail: (id: string) => [...queryKeys.all, 'referenceDetail', id],
};

export const useListSourcesApi = () => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.sources(),
    queryFn: async () => {
      const response = await client.regulatoryWatch.listSources();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch sources');
    },
  });
};

export const useListSourcesWithSubscriptionsApi = () => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.sourcesWithSubscriptions(),
    queryFn: async () => {
      const response =
        await client.regulatoryWatch.listSourcesWithSubscriptions();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch sources with subscriptions');
    },
  });
};

export const useListStandardSourcesApi = (query: ListStandardSourcesQuery) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.standardSources(query),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await client.regulatoryWatch.listStandardSources({
        query,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch standard sources');
    },
  });
};

export const useListUpdatesApi = (
  filters?: ListUpdatesQuery,
  options?: { refetchInterval?: number | false }
) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.updates(filters),
    placeholderData: keepPreviousData,
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const response = await client.regulatoryWatch.listUpdates({
        query: filters ?? {},
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch regulatory updates');
    },
  });
};

export const useUpdateContextStatusApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateContextStatusBody;
    }) => {
      const response = await client.regulatoryWatch.updateContextStatus({
        params: { id },
        body,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update context status');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useUpdateContextAssigneesApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      memberIds,
    }: {
      id: string;
      memberIds: string[];
    }) => {
      const response = await client.regulatoryWatch.updateContextAssignees({
        params: { id },
        body: { memberIds },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update assignees');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useUpdateSourceSubscriptionAssigneesApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceId,
      memberIds,
    }: {
      sourceId: string;
      memberIds: string[];
    }) => {
      const response =
        await client.regulatoryWatch.updateSourceSubscriptionAssignees({
          params: { sourceId },
          body: { memberIds },
        });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update source assignees');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

/** Applies many status updates in one round-trip batch, then invalidates once. */
export const useBulkApplyContextStatusesApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      items: {
        id: string;
        status: NonNullable<UpdateContextStatusBody['status']>;
      }[]
    ) => {
      const results = await Promise.all(
        items.map(({ id, status }) =>
          client.regulatoryWatch.updateContextStatus({
            params: { id },
            body: { status },
          })
        )
      );
      for (const response of results) {
        if (response.status !== 200) {
          throw new Error('Failed to update context status');
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useGetProfileApi = (options?: {
  refetchInterval?: number | false;
}) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.profile(),
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const response = await client.regulatoryWatch.getProfile();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch profile');
    },
  });
};

export const useUpdateProfileApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateProfileBody) => {
      const response = await client.regulatoryWatch.updateProfile({ body });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update profile');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
};

export const useOnboardingGenerateProfileApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: OnboardingGenerateProfileBody) => {
      const response = await client.regulatoryWatch.onboardingGenerateProfile({
        body,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to generate profile from website');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
};

export const useOnboardingClearSubscriptionsApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async () => {
      const response =
        await client.regulatoryWatch.onboardingClearSubscriptions({
          body: undefined,
        });
      if (response.status === 200) return response.body;
      throw new Error('Failed to clear subscriptions');
    },
  });
};

const makeOnboardingRecommendHook =
  (
    route:
      | 'onboardingRecommendRegulations'
      | 'onboardingRecommendGuidances'
      | 'onboardingRecommendStandards'
      | 'onboardingRecommendNews'
  ) =>
  () => {
    const { client } = useAuthenticatedClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async () => {
        const response = await client.regulatoryWatch[route]({
          body: undefined,
        });
        if (response.status === 200) return response.body;
        throw new Error(`Failed: ${route}`);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sourcesWithSubscriptions(),
        });
      },
    });
  };

export const useOnboardingRecommendRegulationsApi = makeOnboardingRecommendHook(
  'onboardingRecommendRegulations'
);
export const useOnboardingRecommendGuidancesApi = makeOnboardingRecommendHook(
  'onboardingRecommendGuidances'
);
export const useOnboardingRecommendStandardsApi = makeOnboardingRecommendHook(
  'onboardingRecommendStandards'
);
export const useOnboardingRecommendNewsApi = makeOnboardingRecommendHook(
  'onboardingRecommendNews'
);

export const useOnboardingCompleteProfileApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await client.regulatoryWatch.onboardingCompleteProfile({
        body: undefined,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to complete onboarding profile');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
      queryClient.invalidateQueries({ queryKey: queryKeys.references() });
    },
  });
};

export const useCreateProductApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateProductBody) => {
      const response = await client.regulatoryWatch.createProduct({ body });
      if (response.status === 201) {
        return response.body;
      }
      throw new Error('Failed to create product');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
};

export const useUpdateProductApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      body,
    }: {
      productId: string;
      body: UpdateProductBody;
    }) => {
      const response = await client.regulatoryWatch.updateProduct({
        params: { productId },
        body,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update product');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
};

export const useDeleteProductApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: string) => {
      const response = await client.regulatoryWatch.deleteProduct({
        params: { productId },
        body: undefined,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete product');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
};

export const useListReferencesApi = (filters?: ListReferencesQuery) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.references(filters),
    queryFn: async () => {
      const response = await client.regulatoryWatch.listReferences({
        query: filters ?? {},
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch references');
    },
  });
};

export const useBulkUpdateSubscriptionsApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: BulkUpdateSubscriptionsBody) => {
      const response = await client.regulatoryWatch.bulkUpdateSubscriptions({
        body,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update subscriptions');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useAttachFileToVersionApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      versionId,
      body,
    }: {
      versionId: string;
      body: AttachFileToVersionBody;
    }) => {
      const response = await client.regulatoryWatch.attachFileToVersion({
        params: { versionId },
        body,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to attach file to version');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useDetachFileFromVersionApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      versionId,
      fileId,
    }: {
      versionId: string;
      fileId: string;
    }) => {
      const response = await client.regulatoryWatch.detachFileFromVersion({
        params: { versionId, fileId },
        body: undefined as never,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to detach file from version');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useGenerateRedlineApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateId: string) => {
      const response = await client.regulatoryWatch.generateRedline({
        params: { updateId },
        body: {},
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to generate redline');
    },
    onSuccess: (_data, updateId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.updateDetail(updateId),
      });
    },
  });
};

export const useGetUpdateDetailApi = (
  updateId: string | undefined,
  options?: { refetchInterval?: number | false }
) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: updateId
      ? queryKeys.updateDetail(updateId)
      : ['regulatoryWatch', 'updateDetail', 'disabled'],
    refetchInterval: options?.refetchInterval,
    queryFn: updateId
      ? async () => {
          const response = await client.regulatoryWatch.getUpdateDetail({
            params: { id: updateId },
          });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch update detail');
        }
      : skipToken,
  });
};

export const useGenerateImpactApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contextId,
    }: {
      contextId: string;
      updateId: string;
    }) => {
      const response = await client.regulatoryWatch.generateImpact({
        params: { id: contextId },
        body: {},
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to generate impact assessment');
    },
    onMutate: ({ contextId, updateId }) => {
      // Optimistically set aiImpactStatus to 'generating' in both caches
      queryClient.setQueryData<GetUpdateDetailResponse>(
        queryKeys.updateDetail(updateId),
        old => {
          if (!old?.context) return old;
          return {
            ...old,
            context: { ...old.context, aiImpactStatus: 'generating' },
          };
        }
      );

      queryClient.setQueriesData<ListUpdatesResponse[]>(
        { queryKey: [...queryKeys.all, 'updates'] },
        old => {
          if (!old) return old;
          return old.map(u =>
            u.context?.id === contextId
              ? {
                  ...u,
                  context: {
                    ...u.context,
                    aiImpactStatus: 'generating' as const,
                  },
                }
              : u
          );
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useExportUpdatesApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (
      filters: ListUpdatesQuery
    ): Promise<ExportUpdatesResponse> => {
      const response = await client.regulatoryWatch.exportUpdates({
        query: filters,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to export updates');
    },
  });
};

export const useExportUpdatesToTracexApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (
      filters: ListUpdatesQuery
    ): Promise<ExportUpdatesToTracexResponse> => {
      const response = await client.regulatoryWatch.exportUpdatesToTracex({
        body: filters,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to export updates to TraceX');
    },
  });
};

export const useResetOnboardingApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await client.regulatoryWatch.resetOnboarding({
        body: undefined,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to reset onboarding');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};

export const useListAllDocumentsApi = (filters?: ListAllDocumentsQuery) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.allDocuments(filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await client.regulatoryWatch.listAllDocuments({
        query: filters ?? {},
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch all documents');
    },
  });
};

export const useGetReferenceDetailApi = (referenceId: string | undefined) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: referenceId
      ? queryKeys.referenceDetail(referenceId)
      : ['regulatoryWatch', 'referenceDetail', 'disabled'],
    queryFn: referenceId
      ? async () => {
          const response = await client.regulatoryWatch.getReferenceDetail({
            params: { referenceId },
          });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch reference detail');
        }
      : skipToken,
  });
};

// ─── Reference subscriptions ────────────────────────────────────────────

export const useListReferenceSubscriptionsApi = (sourceId: string) => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.referenceSubscriptions(sourceId),
    queryFn: async () => {
      const response = await client.regulatoryWatch.listReferenceSubscriptions({
        params: { sourceId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch reference subscriptions');
    },
  });
};

export const useUpdateReferenceSubscriptionApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      referenceId,
      body,
    }: {
      referenceId: string;
      body: UpdateReferenceSubscriptionBody;
    }) => {
      const response = await client.regulatoryWatch.updateReferenceSubscription(
        {
          params: { referenceId },
          body,
        }
      );
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update reference subscription');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
};
